import { Button } from '@/components/Button';
import LoadingSpinner from 'components/LoadingSpinner';
import { EditorSteps } from '../constants';
import { useTileEditorStepsContext } from '../contexts/TileEditorStepsContext';
import { useTileEditorStore } from '../state/TileEditorStoreProvider';

interface StepProgressButtonProps {
    disabled?: boolean;
    isLoading?: boolean;
}

export const StepProgressButton: React.FC<StepProgressButtonProps> = ({ disabled, isLoading }) => {
    const {
        isConfigured,
        isConfiguredAfterCurrentStep,
        isConfigurable,
        currentEditorStep,
        canMoveToNextStep,
        isDocked,
        isEditingSQL,
        nextStep,
        setCurrentEditorStep,
        setIsDocked
    } = useTileEditorStepsContext();

    const { visitedSteps, isNewTile } = useTileEditorStore((s) => ({
        visitedSteps: s.visitedSteps,
        isNewTile: s.isNewTile
    }));

    const isDone =
        (isConfigured || // Tile is fully configured
            isConfiguredAfterCurrentStep || // Completing the current step will mean the tile is configured
            isEditingSQL) && // If on the SQL tab
        !(
            // Don't show done if the datastream is configurable and
            // the parameters tab hasn't yet been visited for new tiles
            (isNewTile && isConfigurable && !visitedSteps.includes(EditorSteps.parameters))
        );

    const isMetaDataStep = [EditorSteps.metadata, EditorSteps.tileMetadata].includes(currentEditorStep as EditorSteps);

    return (
        <Button
            disabled={disabled || (!canMoveToNextStep && !isMetaDataStep)}
            {...(isLoading && { icon: <LoadingSpinner size={18} /> })}
            onClick={() => {
                isDone ? setCurrentEditorStep(undefined) : nextStep();

                // Make sure we undock the step if we've now finished configuration
                if (isDocked && isDone) {
                    setIsDocked?.(false);
                }
            }}
            data-testid='tileEditorProgressButton'
        >
            {isDone ? 'Done' : 'Next'}
        </Button>
    );
};
